import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  TextField,
  Avatar,
  Button,
  Dialog,
  DialogTitle,
  DialogActions,
  Pagination,
} from "@mui/material";
import { useParams } from "react-router-dom";
import "../../../../assets/styles/Bidding/PlayersTab.css";
import { BACKEND_DOMAIN } from "../../../../common/env";

const PlayersTab = () => {
  const [players, setPlayers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [playersPerPage] = useState(8);
  const { auctionId } = useParams();
  const [selectPlayer, setSelectedPlayer] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${BACKEND_DOMAIN}/auctions/${auctionId}/players`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPlayers(response.data.players);
      } catch (error) {
        console.error("Error fetching players:", error);
      }
    };

    fetchPlayers();
  }, [auctionId]);

  // Filter players based on search term
  const filteredPlayers = players.filter(
    (player) =>
      player.customID.toLowerCase().includes(searchTerm) ||
      player.playerName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination logic
  const indexOfLastPlayer = page * playersPerPage;
  const indexOfFirstPlayer = indexOfLastPlayer - playersPerPage;
  const currentPlayers = filteredPlayers.slice(indexOfFirstPlayer, indexOfLastPlayer);
  const totalPages = Math.ceil(filteredPlayers.length / playersPerPage);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const handleOpenDialog = async (player) => {
    setSelectedPlayer(player);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPlayer(null);
  };

  return (
    <Box
      className="players-tab-container"
      sx={{
        padding: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
        margin: 0
      }}
    >
      {/* Search Bar - Centered with top margin */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center',
        width: '100%',
        maxWidth: "1200px",
        margin: "16px auto 10px", // Added top margin
        position: "relative",
        top: 0,
        padding: { xs: '0 16px', sm: 0 }
      }}>
        <TextField
          label="Search by ID or Name"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ 
            width: { xs: "200px", sm: "200px" },
            maxWidth: { xs: "200px", sm: "200px" },
            '& .MuiInputBase-root': {
              height: '40px', // Make the input smaller
              alignItems: 'center'
            },
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px' // More rounded corners
            },
            '& .MuiInputLabel-root': {
              transform: 'translate(14px, 10px) scale(1)', // Better label alignment
              fontSize: '0.9rem'
            },
            '& .MuiInputLabel-shrink': {
              transform: 'translate(14px, -6px) scale(0.75)' // Proper shrink alignment
            }
          }}
        />
      </Box>

      {/* Players Grid */}
      {currentPlayers.length > 0 ? (
        <>
          <Grid container spacing={2} className="players-grid" sx={{ 
            maxWidth: "1200px", 
            position: "relative", 
            top: 0,
            width: "100%",
            margin: "0 auto",
            marginTop: "10px",
            padding: { xs: 1, sm: 0 }
          }}>
            {currentPlayers.map((player) => (
              <Grid item xs={12} sm={6} md={4} key={player._id}>
                <Card className="player-card-compact">
                  <Box className="player-content-wrapper" sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch', height: '100%' }}>
                    {/* Player Logo Section - Left */}
                    <Box className="player-logo-section" sx={{ 
                      width: '30%', 
                      minWidth: '80px', 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center', 
                      padding: '12px',
                      borderRight: '1px solid #e2e8f0'
                    }}>
                      {player.playerLogo ? (
                        <Box
                          component="img"
                          src={`${player.playerLogo}`}
                          alt={player.playerName}
                          sx={{ 
                            width: '100%', 
                            height: '100%', 
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                        />
                      ) : (
                        <Box
                          sx={{ 
                            width: '100%', 
                            height: '100%', 
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backgroundColor: '#f0f0f0',
                            borderRadius: '8px',
                            fontSize: '1.5rem',
                            fontWeight: 'bold',
                            color: '#666'
                          }}
                        >
                          {player.playerName.charAt(0).toUpperCase()}
                        </Box>
                      )}
                    </Box>

                    {/* Player Details Section - Right */}
                    <Box className="player-details-section" sx={{ width: '70%', padding: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                      <CardContent className="player-details-compact" sx={{ padding: "0", flex: '1' }}>
                        <Typography className="player-name-compact" sx={{ fontSize: "1rem", fontWeight: "bold", marginBottom: "4px" }}>
                          {player.playerName}
                        </Typography>
                        <Typography className="player-info-compact" sx={{ fontSize: "0.8rem", color: "#666", marginBottom: "2px" }}>
                          <strong>ID:</strong> {player.customID}
                        </Typography>
                        <Typography className="player-info-compact" sx={{ fontSize: "0.8rem", color: "#666", marginBottom: "2px" }}>
                          <strong>Type:</strong> {player.playerType}
                        </Typography>
                        <Typography className="player-info-compact" sx={{ fontSize: "0.8rem", color: "#666", marginBottom: "8px" }}>
                          <strong>Base:</strong> ${player.baseValue}
                        </Typography>
                      </CardContent>
                      <Button
                        size="small"
                        variant="contained"
                        color="primary"
                        onClick={() => handleOpenDialog(player)}
                        sx={{ fontSize: "0.75rem", padding: "4px 12px", alignSelf: 'flex-start' }}
                      >
                        View Details
                      </Button>
                    </Box>
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 2, mt: 2, mb: 2 }}>
            {/* Prev */}
            {page > 1 && (
              <Typography 
                onClick={() => handlePageChange(null, page - 1)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '1.075rem',
                  fontWeight: 500,
                  color: '#3f51b5',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#303f9f',
                  }
                }}
              >
                &lt; Prev
              </Typography>
            )}

            {/* Visible pages: current ± 1 */}
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p >= page - 1 && p <= page + 1)
              .map(p => (
                <Typography
                  key={p}
                  onClick={() => handlePageChange(null, p)}
                  sx={{
                    cursor: 'pointer',
                    fontSize: '1.075rem',
                    fontWeight: p === page ? 600 : 400,
                    color: p === page ? '#3f51b5' : '#666',
                    textDecoration: p === page ? 'underline' : 'none',
                    '&:hover': {
                      color: '#303f9f',
                      textDecoration: 'underline',
                    }
                  }}
                >
                  {p}
                </Typography>
              ))}

            {/* Next */}
            {page < totalPages && (
              <Typography 
                onClick={() => handlePageChange(null, page + 1)}
                sx={{
                  cursor: 'pointer',
                  fontSize: '1.075rem',
                  fontWeight: 500,
                  color: '#3f51b5',
                  '&:hover': {
                    textDecoration: 'underline',
                    color: '#303f9f',
                  }
                }}
              >
                Next &gt;
              </Typography>
            )}
          </Box>

        </>
      ) : (
        <Typography className="no-players-message" sx={{ position: "relative", top: "-20px" }}>
          No players match your search.
        </Typography>
      )}

      {/* OBS-Style Player Details Dialog */}
      {selectPlayer && (
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
          fullScreen={false}
          sx={{
            "& .MuiDialog-paper": {
              borderRadius: "20px",
              background: "linear-gradient(145deg, rgba(15, 25, 50, 0.95), rgba(30, 50, 90, 0.95))",
              border: "3px solid rgba(255, 255, 255, 0.3)",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
              backdropFilter: "blur(15px)",
              color: "white",
              margin: { xs: "16px", sm: "32px" },
              width: { xs: "calc(100% - 32px)", sm: "auto" },
              maxWidth: { xs: "100%", sm: "md" }
            },
          }}
        >
          <DialogTitle sx={{ textAlign: "center", padding: { xs: "20px", sm: "30px 30px 20px" } }}>
            <Box sx={{ 
              display: "flex", 
              alignItems: "center", 
              gap: { xs: 2, sm: 3 }, 
              justifyContent: "center",
              flexDirection: { xs: "column", sm: "row" }
            }}>
              <Avatar
                src={`${selectPlayer.playerLogo}`}
                sx={{
                  width: { xs: 100, sm: 140 },
                  height: { xs: 100, sm: 140 },
                  border: "4px solid #ffffff",
                  boxShadow: "0 10px 30px rgba(0, 0, 0, 0.4)",
                }}
              />
              <Box sx={{ textAlign: { xs: "center", sm: "left" } }}>
                <Typography sx={{ 
                  fontSize: { xs: "1.5rem", sm: "2rem" }, 
                  fontWeight: "bold", 
                  color: "#ffffff", 
                  marginBottom: "8px" 
                }}>
                  {selectPlayer.playerName}
                </Typography>
                <Typography sx={{ 
                  fontSize: { xs: "1rem", sm: "1.1rem" }, 
                  color: "#64b5f6", 
                  fontWeight: 600, 
                  textTransform: "uppercase", 
                  letterSpacing: "2px", 
                  marginBottom: "8px" 
                }}>
                  {selectPlayer.playerType}
                </Typography>
                <Typography sx={{ 
                  fontSize: { xs: "1.1rem", sm: "1.3rem" }, 
                  color: "#ffd54f", 
                  fontWeight: "bold" 
                }}>
                  Base Value: ${selectPlayer.baseValue}
                </Typography>
              </Box>
            </Box>
          </DialogTitle>

          <Box sx={{ 
            padding: { xs: "0 15px 15px", sm: "0 30px 20px" }, 
            display: "flex", 
            flexDirection: "column", 
            gap: { xs: 1, sm: 2 } 
          }}>
            {[
              { label: "MATCHES", value: selectPlayer.stats?.matches || 0 },
              { label: "RUNS", value: selectPlayer.stats?.runs || 0 },
              { label: "WICKETS", value: selectPlayer.stats?.wickets || 0 },
            ].map((stat, index) => (
              <Box
                key={index}
                sx={{
                  background: "rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  padding: { xs: "6px 10px", sm: "8px 15px" },
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderLeft: "4px solid #64b5f6",
                  minHeight: { xs: "35px", sm: "40px" },
                }}
              >
                <Typography sx={{ 
                  color: "rgba(255, 255, 255, 0.8)", 
                  fontSize: { xs: "0.8rem", sm: "0.9rem" }, 
                  fontWeight: 600, 
                  textTransform: "uppercase", 
                  letterSpacing: "1px" 
                }}>
                  {stat.label}
                </Typography>
                <Typography sx={{ 
                  color: "#ffffff", 
                  fontSize: { xs: "1.1rem", sm: "1.4rem" }, 
                  fontWeight: "bold", 
                  textShadow: "1px 1px 2px rgba(0, 0, 0, 0.5)" 
                }}>
                  {stat.value}
                </Typography>
              </Box>
            ))}
          </Box>

          <DialogActions sx={{ 
            padding: { xs: "15px 20px 20px", sm: "20px 30px 30px" }, 
            justifyContent: "center" 
          }}>
            <Button
              onClick={handleCloseDialog}
              variant="contained"
              sx={{
                background: "linear-gradient(45deg, #3f51b5, #5c6bc0)",
                color: "white",
                padding: { xs: "8px 20px", sm: "10px 30px" },
                borderRadius: "25px",
                fontSize: { xs: "0.9rem", sm: "1rem" },
                fontWeight: "bold",
                "&:hover": {
                  background: "linear-gradient(45deg, #303f9f, #3f51b5)",
                },
                minWidth: { xs: "120px", sm: "auto" }
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

export default PlayersTab;